import { Variable } from 'src/api/types';
import {
  findVariableByToken,
  getMissingVariableTokens,
  isPlainNumberFormula,
} from 'src/components/FormulaInput/shared/formula';

/**
 * Where a cost is being displayed. RMP surfaces show evaluated amounts (BE-computed);
 * library surfaces never show computed amounts — a variable-bearing formula renders as
 * a "Formula" marker there (product decision on CRPD-7209, see the Figma "Framework
 * templates" table: value / "Formula" + icon / "–").
 */
export type CostDisplayMode = 'rmp' | 'library';

export type CostDisplay =
  | { state: 'value'; value: number; calculated: boolean }
  | { state: 'missing' }
  | { state: 'formula' }
  | { state: 'blank' };

interface ResolveCostDisplayArgs {
  /** Evaluated (formula-bearing) or literal cost. 0 is a real cost — only null/undefined mean absent. */
  value: number | null | undefined;
  formula: string | null | undefined;
  mode: CostDisplayMode;
}

/**
 * Single source of truth mapping a control's cost fields to a display state.
 * A formula whose evaluated value is absent means an unpopulated variable — that is
 * the red "Missing variables" state, never 0.
 */
export const resolveCostDisplay = ({ value, formula, mode }: ResolveCostDisplayArgs): CostDisplay => {
  const hasFormula = formula != null && String(formula).trim() !== '';

  if (!hasFormula) {
    return value != null ? { state: 'value', value, calculated: false } : { state: 'blank' };
  }

  if (isPlainNumberFormula(formula)) {
    return { state: 'value', value: value ?? Number(formula), calculated: false };
  }

  if (mode === 'library') {
    return { state: 'formula' };
  }

  return value != null ? { state: 'value', value, calculated: true } : { state: 'missing' };
};

/**
 * Sort key for cost columns: the displayed amount, or null for missing/formula/blank
 * states so tanstack's `sortUndefined` groups the non-numeric states together.
 */
export const costSortValue = (display: CostDisplay): number | null => {
  return display.state === 'value' ? display.value : null;
};

/**
 * Human-readable names of a formula's missing variables (for the pill's hover
 * tooltip — epic AC3): resolved variables show their alias/name, unresolved
 * tokens are prettified (underscores → spaces).
 */
export const getMissingVariableNames = (
  formula: string | null | undefined,
  variables: Variable[],
): string[] => {
  return getMissingVariableTokens(formula, variables).map((token) => {
    const variable = findVariableByToken(variables, token);
    if (variable) return variable.alias ?? variable.name;
    const pretty = token.replace(/_/g, ' ');
    return pretty.charAt(0).toUpperCase() + pretty.slice(1);
  });
};

export const COST_SYNTAX_ERROR_MESSAGE = 'Only numbers, + - * / ( ) and #variables are supported';

/**
 * Cost formulas are pinned to the FE/BE grammar intersection: `+ - * / ( )`,
 * decimal literals and `#` variable tokens. The FE evaluator would happily accept
 * `^`, `%` and function names (log, sin, …) that the BE evaluator rejects — a
 * formula must not preview a Result the save then can't honour.
 */
export const findUnsupportedCostSyntax = (expression?: string | null): boolean => {
  if (!expression) return false;
  return /[A-Za-z^%]/.test(String(expression).replace(/#[A-Za-z0-9_]+/g, ''));
};

/**
 * CRPD-7287 (I15) — Jennifer: "It would be nicer to quote the string causing the issue."
 * Returns the first fragment that `findUnsupportedCostSyntax` tripped on, already quoted, so a
 * CSV error can name it. Typically this is the tail of a variable name broken by a space
 * (`#Number of_staff*6` → `"of_staff"`) or an unsupported operator (`5%` → `"%"`).
 */
export const quoteOffendingToken = (expression?: string | null): string => {
  const stripped = String(expression ?? '').replace(/#[A-Za-z0-9_]+/g, ' ');
  const match = stripped.match(/[A-Za-z_][A-Za-z0-9_]*|[\^%]/);
  return `"${match ? match[0] : String(expression ?? '').trim()}"`;
};
